import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BackofficeLayout from '@/components/backoffice/BackofficeLayout';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import {
  getChamadoById,
  atualizarStatusComAuditoria,
  atribuirResponsavelComAuditoria,
} from '@/mocks/backoffice/atendimentoAuditoria';
import TicketChat from './components/TicketChat';
import UserProfilePanel from './components/UserProfilePanel';

export default function BackofficeAtendimentoDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session } = useBackofficeAuth();
  const [chamado, setChamado] = useState(getChamadoById(id || ''));
  const [tabAtiva, setTabAtiva] = useState<'chat' | 'detalhes'>('chat');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEncerrarModal, setShowEncerrarModal] = useState(false);
  const [showReabrirModal, setShowReabrirModal] = useState(false);
  const [statusFeedback, setStatusFeedback] = useState('');

  const usuarioNome = session.nome || 'Usuário';
  const usuarioRole = session.role || 'operador';

  const recarregarChamado = () => {
    setChamado(getChamadoById(id || ''));
  };

  useEffect(() => {
    recarregarChamado();
  }, [id]);

  if (!chamado) {
    return (
      <BackofficeLayout>
        <div className="p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-gray-300"></i>
          </div>
          <p className="text-sm text-gray-400">Chamado não encontrado</p>
          <button
            onClick={() => navigate('/backoffice/atendimento')}
            className="mt-4 px-4 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 transition-all cursor-pointer"
          >
            Voltar para atendimento
          </button>
        </div>
      </BackofficeLayout>
    );
  }

  const statusConfig: Record<string, { bg: string; text: string; label: string; dot: string }> = {
    aberto: { bg: 'bg-red-50', text: 'text-red-600', label: 'Aberto', dot: 'bg-red-500' },
    em_andamento: { bg: 'bg-blue-50', text: 'text-blue-600', label: 'Em andamento', dot: 'bg-blue-500' },
    aguardando: { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Aguardando', dot: 'bg-amber-500' },
    resolvido: { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Resolvido', dot: 'bg-emerald-500' },
    fechado: { bg: 'bg-gray-50', text: 'text-gray-500', label: 'Fechado', dot: 'bg-gray-400' },
  };

  const tipoConfig: Record<string, { icon: string; label: string }> = {
    suporte_tecnico: { icon: 'ri-computer-line', label: 'Suporte Técnico' },
    financeiro: { icon: 'ri-money-cny-circle-line', label: 'Financeiro' },
    duvida: { icon: 'ri-question-line', label: 'Dúvida' },
    reclamacao: { icon: 'ri-emotion-unhappy-line', label: 'Reclamação' },
  };

  const prioridadeConfig: Record<string, { bg: string; text: string }> = {
    baixa: { bg: 'bg-gray-100', text: 'text-gray-600' },
    media: { bg: 'bg-blue-50', text: 'text-blue-600' },
    alta: { bg: 'bg-orange-50', text: 'text-orange-600' },
    urgente: { bg: 'bg-red-50', text: 'text-red-600' },
  };

  const usuarios = [
    { id: 'adm-001', nome: 'Administrador Geral', role: 'admin' },
    { id: 'sup-001', nome: 'Ana Souza', role: 'suporte' },
    { id: 'fin-001', nome: 'Carlos Mendes', role: 'financeiro' },
    { id: 'op-001', nome: 'Mariana Lima', role: 'operador' },
  ];

  const handleStatusChange = (novoStatus: string) => {
    if (atualizarStatusComAuditoria(chamado.id, novoStatus as any, usuarioNome, usuarioRole)) {
      recarregarChamado();
      setShowStatusModal(false);
      setStatusFeedback('Status atualizado!');
      setTimeout(() => setStatusFeedback(''), 2000);
    }
  };

  const handleAssign = (responsavel: string) => {
    if (atribuirResponsavelComAuditoria(chamado.id, responsavel, usuarioNome, usuarioRole)) {
      recarregarChamado();
      setShowAssignModal(false);
      setStatusFeedback('Responsável atualizado!');
      setTimeout(() => setStatusFeedback(''), 2000);
    }
  };

  const handleEncerrar = () => {
    if (atualizarStatusComAuditoria(chamado.id, 'fechado' as any, usuarioNome, usuarioRole)) {
      recarregarChamado();
      setShowEncerrarModal(false);
      setStatusFeedback('Atendimento encerrado!');
      setTimeout(() => setStatusFeedback(''), 3000);
    }
  };

  const handleResolver = () => {
    if (atualizarStatusComAuditoria(chamado.id, 'resolvido' as any, usuarioNome, usuarioRole)) {
      recarregarChamado();
      setStatusFeedback('Chamado resolvido!');
      setTimeout(() => setStatusFeedback(''), 3000);
    }
  };

  const handleAguardar = () => {
    if (atualizarStatusComAuditoria(chamado.id, 'aguardando' as any, usuarioNome, usuarioRole)) {
      recarregarChamado();
      setStatusFeedback('Marcado como aguardando');
      setTimeout(() => setStatusFeedback(''), 3000);
    }
  };

  const handleReabrir = () => {
    if (atualizarStatusComAuditoria(chamado.id, 'aberto' as any, usuarioNome, usuarioRole)) {
      recarregarChamado();
      setShowReabrirModal(false);
      setStatusFeedback('Atendimento reaberto!');
      setTimeout(() => setStatusFeedback(''), 3000);
    }
  };

  const isFinalizado = chamado.status === 'fechado' || chamado.status === 'resolvido';
  const isEmAndamento = chamado.status === 'em_andamento' || chamado.status === 'aberto';

  const st = statusConfig[chamado.status] || statusConfig.aberto;
  const tp = tipoConfig[chamado.tipo] || tipoConfig.duvida;
  const pr = prioridadeConfig[chamado.prioridade] || prioridadeConfig.baixa;

  return (
    <BackofficeLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <button
              onClick={() => navigate('/backoffice/atendimento')}
              className="flex items-center space-x-1 text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer mb-2"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Voltar para atendimento</span>
            </button>
            <div className="flex items-center space-x-2">
              <h1 className="text-xl font-bold text-gray-800">{chamado.id}</h1>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${st.bg} ${st.text}`}>
                {st.label}
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2 flex-wrap">
            {statusFeedback && (
              <span className="text-xs text-emerald-600 font-bold px-2 py-1 bg-emerald-50 rounded">
                {statusFeedback}
              </span>
            )}

            {/* Acoes rapidas */}
            {isEmAndamento && (
              <button
                onClick={handleAguardar}
                className="px-3 py-2 bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-xs"></i>
                </div>
                <span>Aguardar</span>
              </button>
            )}

            {isEmAndamento && (
              <button
                onClick={handleResolver}
                className="px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-double-line text-xs"></i>
                </div>
                <span>Resolver</span>
              </button>
            )}

            {!isFinalizado && (
              <button
                onClick={() => setShowEncerrarModal(true)}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-close-circle-line text-xs"></i>
                </div>
                <span>Encerrar</span>
              </button>
            )}

            {isFinalizado && (
              <button
                onClick={() => setShowReabrirModal(true)}
                className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-restart-line text-xs"></i>
                </div>
                <span>Reabrir</span>
              </button>
            )}

            <div className="w-px h-6 bg-gray-200 mx-1 hidden sm:block"></div>

            <button
              onClick={() => setShowStatusModal(true)}
              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              <span>Alterar status</span>
            </button>
            <button
              onClick={() => setShowAssignModal(true)}
              className="px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 whitespace-nowrap"
            >
              <i className="ri-user-add-line"></i>
              <span>Atribuir</span>
            </button>
          </div>
        </div>

        {/* Main grid: Chat + Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Chat / Content */}
          <div className="lg:col-span-2 space-y-4">
            {/* Title card */}
            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-lg bg-stone-700 flex items-center justify-center shrink-0">
                  <i className={`${tp.icon} text-white text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-sm font-bold text-gray-800">{chamado.titulo}</h2>
                  <p className="text-xs text-gray-500 mt-1">{chamado.descricao}</p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-100 bg-white rounded-t-xl px-1 pt-1">
              {([
                { id: 'chat' as const, label: 'Chat em Tempo Real', icon: 'ri-chat-3-line' },
                { id: 'detalhes' as const, label: 'Detalhes do Chamado', icon: 'ri-file-list-line' },
              ]).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setTabAtiva(tab.id)}
                  className={`flex items-center space-x-1.5 px-4 py-3 text-xs font-bold transition-all cursor-pointer ${
                    tabAtiva === tab.id
                      ? 'text-emerald-600 border-b-2 border-emerald-500'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <i className={tab.icon}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {tabAtiva === 'chat' && (
              <TicketChat chamado={chamado} onUpdate={recarregarChamado} />
            )}

            {tabAtiva === 'detalhes' && (
              <div className="bg-white rounded-b-xl rounded-t-none border border-gray-100 p-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tipo</p>
                    <div className="flex items-center space-x-1.5 mt-1">
                      <i className={`${tp.icon} text-gray-500 text-xs`}></i>
                      <span className="text-xs font-medium text-gray-700">{tp.label}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Prioridade</p>
                    <span className={`inline-flex mt-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${pr.bg} ${pr.text}`}>
                      {chamado.prioridade}
                    </span>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Abertura</p>
                    <p className="text-xs text-gray-700 mt-1">{new Date(chamado.data_criacao).toLocaleString('pt-BR')}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Última atualização</p>
                    <p className="text-xs text-gray-700 mt-1">{new Date(chamado.data_atualizacao).toLocaleString('pt-BR')}</p>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Responsável</p>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-white">{chamado.responsavel?.charAt(0) || '?'}</span>
                    </div>
                    <span className="text-xs font-medium text-gray-700">{chamado.responsavel || 'Não atribuído'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar: Perfil do usuário */}
          <div className="space-y-4">
            <UserProfilePanel
              usuarioEmail={chamado.usuario_email}
              usuarioNome={chamado.usuario_nome}
              usuarioTipo={chamado.usuario_tipo}
            />

            {/* Quick info card */}
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Info do Chamado</h3>
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">Status</span>
                  <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${st.bg} ${st.text}`}>
                    {st.label}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">Prioridade</span>
                  <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${pr.bg} ${pr.text}`}>
                    {chamado.prioridade}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">Tipo</span>
                  <span className="text-xs text-gray-700">{tp.label}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">Responsável</span>
                  <span className="text-xs font-medium text-gray-700">{chamado.responsavel || '-'}</span>
                </div>
                <div className="border-t border-gray-100 pt-2">
                  <span className="text-[10px] text-gray-400">Aberto em</span>
                  <p className="text-xs text-gray-700">{new Date(chamado.data_criacao).toLocaleString('pt-BR')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status Modal */}
        {showStatusModal && (
          <>
            <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowStatusModal(false)}></div>
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
                <h3 className="text-sm font-bold text-gray-800 mb-4">Alterar Status</h3>
                <div className="space-y-2">
                  {(['aberto', 'em_andamento', 'aguardando', 'resolvido', 'fechado'] as const).map((s) => {
                    const cfg = statusConfig[s];
                    return (
                      <button
                        key={s}
                        onClick={() => handleStatusChange(s)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-all cursor-pointer ${
                          chamado.status === s ? 'ring-2 ring-emerald-500 bg-emerald-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <span className={`w-2 h-2 rounded-full ${cfg.dot}`}></span>
                          <span className="text-xs font-medium text-gray-700">{cfg.label}</span>
                        </div>
                        {chamado.status === s && <i className="ri-check-line text-emerald-600 text-sm"></i>}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="mt-4 w-full py-2 text-xs font-bold text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </>
        )}

        {/* Assign Modal */}
        {showAssignModal && (
          <>
            <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowAssignModal(false)}></div>
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
                <h3 className="text-sm font-bold text-gray-800 mb-4">Atribuir Responsável</h3>
                <div className="space-y-2">
                  {usuarios.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => handleAssign(u.nome)}
                      className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-left transition-all cursor-pointer ${
                        chamado.responsavel === u.nome ? 'ring-2 ring-emerald-500 bg-emerald-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center">
                        <span className="text-[10px] font-bold text-white">{u.nome.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-700">{u.nome}</p>
                        <p className="text-[10px] text-gray-400 capitalize">{u.role}</p>
                      </div>
                      {chamado.responsavel === u.nome && (
                        <i className="ri-check-line text-emerald-600 text-sm ml-auto"></i>
                      )}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="mt-4 w-full py-2 text-xs font-bold text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </>
        )}

        {/* Encerrar Modal */}
        {showEncerrarModal && (
          <>
            <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowEncerrarModal(false)}></div>
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-close-circle-line text-gray-600 text-sm"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800">Encerrar Atendimento</h3>
                    <p className="text-[10px] text-gray-400">Esta acao e irreversivel</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">Chamado</span>
                    <span className="text-xs font-bold text-gray-800">{chamado.id}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">Status atual</span>
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${st.bg} ${st.text}`}>
                      {st.label}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mb-4">
                  O atendimento sera encerrado e movido para Fechado. O participante sera notificado.
                </p>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleEncerrar}
                    className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-800 text-white text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                  >
                    Encerrar Atendimento
                  </button>
                  <button
                    onClick={() => setShowEncerrarModal(false)}
                    className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Reabrir Modal */}
        {showReabrirModal && (
          <>
            <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowReabrirModal(false)}></div>
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-restart-line text-blue-600 text-sm"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800">Reabrir Atendimento</h3>
                    <p className="text-[10px] text-gray-400">Retornar para Aberto</p>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mb-4">
                  O chamado sera reaberto e retornara para o status Aberto. Continue o atendimento normalmente.
                </p>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleReabrir}
                    className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                  >
                    Reabrir
                  </button>
                  <button
                    onClick={() => setShowReabrirModal(false)}
                    className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </BackofficeLayout>
  );
}